// Granite curbs, bins, bus shelters, kiosk, Bicing stations and mailboxes.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { pick, rnd } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add, canvasTex } from '../lib/factory.js';
import { octShape, slabGeo } from '../lib/shapes.js';
import { instanced } from '../lib/instancing.js';
import { textTex } from '../textures/signs.js';
import { nightMats } from '../materials/night.js';
import { PCH, PLATE, blocks, colliders } from './eixample.js';
import { mergeAny, tint } from './facade-details.js';

export function buildSidewalkDetail() {
  // ---------- sidewalk detail: granite curbs, bins, bus shelters, kiosk, Bicing, mailboxes ----------
  {
    const graniteTex = canvasTex(64, 64, (c) => {
      c.fillStyle = '#a9a7a2';
      c.fillRect(0, 0, 64, 64);
      for (let k = 0; k < 900; k++) {
        const v = rnd();
        c.fillStyle = v < 0.4 ? '#8e8c88' : v < 0.8 ? '#c2c0bb' : '#6f6d69';
        c.fillRect(rnd() * 64, rnd() * 64, 1.3, 1.3);
      }
      c.fillStyle = '#7d7b77';
      c.fillRect(0, 0, 1, 64);
    });
    graniteTex.repeat.set(1, 1);
    const gm = M(0xffffff, { map: graniteTex, flatShading: false, roughness: 0.6 });
    for (let i = -1; i <= 1; i++)
      for (let j = -1; j <= 1; j++)
        add(
          slabGeo(octShape(PLATE + 0.1, PCH + 0.04, PLATE - 0.7, PCH - 0.3), 0.012),
          gm,
          i * 30,
          0.362,
          j * 30,
        ).castShadow = false;
    const bins = [];
    for (const b of blocks)
      for (const [sx, sz] of [
        [1, 1],
        [1, -1],
        [-1, 1],
        [-1, -1],
      ])
        bins.push({ x: b.cx + sx * 9.95 + sx * 0.7, z: b.cz + sz * 9.95 - sz * 0.7 });
    instanced(
      mergeAny([
        tint(new THREE.CylinderGeometry(0.03, 0.03, 0.9, 8).translate(0, 0.45, 0), 0x2c3a33),
        tint(new THREE.CylinderGeometry(0.17, 0.15, 0.42, 16).translate(0.19, 0.62, 0), 0x6b7075),
        tint(new THREE.CylinderGeometry(0.18, 0.18, 0.04, 16).translate(0.19, 0.85, 0), 0x3a3f44),
      ]),
      P(0xffffff, { vertexColors: true, roughness: 0.4, metalness: 0.5 }),
      bins,
      (o, b) => o.position.set(b.x, 0.36, b.z),
    );
    // bus shelters on the outer ring
    const posterTex = textTex(128, 256, (c) => {
      const g = c.createLinearGradient(0, 0, 0, 256);
      g.addColorStop(0, '#f2b33d');
      g.addColorStop(1, '#d9412c');
      c.fillStyle = g;
      c.fillRect(0, 0, 128, 256);
      c.fillStyle = '#fff';
      c.font = 'bold 22px Helvetica';
      c.textAlign = 'center';
      c.fillText('BARCELONA', 64, 60);
      c.font = '14px Helvetica';
      c.fillText('Festa de la Mercè', 64, 84);
      c.beginPath();
      c.arc(64, 160, 40, 0, PI * 2);
      c.fillStyle = 'rgba(255,255,255,.85)';
      c.fill();
    });
    const poster = M(0xffffff, {
      map: posterTex,
      emissiveMap: posterTex,
      emissive: 0xffffff,
      emissiveIntensity: 0.15,
      flatShading: false,
    });
    nightMats.push([poster, 1]);
    const shelterGlass = P(0xcfe3ef, {
        roughness: 0.02,
        metalness: 0.1,
        transparent: true,
        opacity: 0.3,
      }),
      steel = P(0x9ea3a8, { roughness: 0.3, metalness: 0.8 });
    for (const [x, z, ry] of [
      [-24, 48.3, PI],
      [21, -48.3, 0],
      [48.3, 8, -PI / 2],
    ]) {
      const g = new THREE.Group();
      g.position.set(x, 0.36, z);
      g.rotation.y = ry;
      scene.add(g);
      add(new RoundedBoxGeometry(3.4, 0.1, 1.4, 2, 0.03), steel, 0, 2.3, 0, g);
      add(new THREE.BoxGeometry(3.2, 1.9, 0.04), shelterGlass, 0, 1.25, -0.6, g).castShadow = false;
      for (const sx of [-1, 1])
        add(new THREE.CylinderGeometry(0.04, 0.04, 2.3, 10), steel, sx * 1.6, 1.15, -0.6, g);
      const ad = add(
        new RoundedBoxGeometry(1.1, 1.9, 0.14, 2, 0.03),
        [steel, steel, steel, steel, poster, poster],
        1.95,
        1.25,
        -0.1,
        g,
      );
      ad.rotation.y = PI / 2;
      add(new RoundedBoxGeometry(2.2, 0.07, 0.4, 2, 0.02), steel, -0.3, 0.5, -0.35, g);
      add(new THREE.CylinderGeometry(0.03, 0.03, 2.6, 8), steel, -2.1, 1.3, 0.55, g);
      add(
        new RoundedBoxGeometry(0.5, 0.5, 0.04, 2, 0.02),
        M(0xd2302a, { flatShading: false }),
        -2.1,
        2.5,
        0.55,
        g,
      );
    }
    // newspaper kiosk by the Arc de Triomf
    {
      const g = new THREE.Group();
      g.position.set(-40, 0.36, -50);
      scene.add(g);
      const kTex = textTex(256, 128, (c) => {
        c.fillStyle = '#2f5b3a';
        c.fillRect(0, 0, 256, 128);
        for (let i = 0; i < 9; i++)
          for (let j = 0; j < 3; j++) {
            c.fillStyle = pick(['#f2f0ea', '#d9534f', '#f0c419', '#3b7dd8', '#2e9e6a']);
            c.fillRect(8 + i * 27, 10 + j * 38, 22, 30);
          }
      });
      add(
        new RoundedBoxGeometry(2.4, 2, 1.7, 2, 0.06),
        [
          M(0x2f5b3a),
          M(0x2f5b3a),
          M(0x2f5b3a),
          M(0x2f5b3a),
          M(0xffffff, { map: kTex, flatShading: false }),
          M(0x2f5b3a),
        ],
        0,
        1,
        0,
        g,
      );
      add(new RoundedBoxGeometry(2.9, 0.12, 2.3, 2, 0.04), M(0x24452c), 0, 2.1, 0.15, g);
      colliders.push({ type: 'rect', x0: -41.3, x1: -38.7, z0: -50.9, z1: -48.9 });
    }
    // Bicing station on the Sagrada plaza: red-and-white shared bikes in their docks
    const bikes = [];
    for (let k = 0; k < 6; k++) bikes.push({ x: 6.6 + k * 0.5, z: 11 });
    const bikeG = mergeAny([
      tint(
        new THREE.TorusGeometry(0.24, 0.035, 8, 24).rotateY(PI / 2).translate(0, 0.26, 0.42),
        0x1b1b1b,
      ),
      tint(
        new THREE.TorusGeometry(0.24, 0.035, 8, 24).rotateY(PI / 2).translate(0, 0.26, -0.42),
        0x1b1b1b,
      ),
      tint(
        new THREE.CylinderGeometry(0.025, 0.025, 0.85, 8)
          .rotateX(PI / 2 + 0.25)
          .translate(0, 0.45, 0),
        0xd2302a,
      ),
      tint(
        new THREE.CylinderGeometry(0.025, 0.025, 0.5, 8).rotateX(-0.3).translate(0, 0.5, 0.32),
        0xd2302a,
      ),
      tint(new THREE.BoxGeometry(0.14, 0.04, 0.24).translate(0, 0.78, -0.22), 0x222222),
      tint(new THREE.BoxGeometry(0.5, 0.03, 0.03).translate(0, 0.82, 0.4), 0x222222),
      tint(new THREE.BoxGeometry(0.26, 0.14, 0.2).translate(0, 0.72, 0.52), 0xf2f0ea),
      tint(new THREE.CylinderGeometry(0.03, 0.04, 0.75, 8).translate(0, 0.37, 0.78), 0x9ea3a8),
    ]);
    instanced(
      bikeG,
      P(0xffffff, { vertexColors: true, roughness: 0.35, metalness: 0.4 }),
      bikes,
      (o, b) => {
        o.position.set(b.x, 0.36, b.z);
        o.rotation.y = PI;
      },
    );
    // yellow Correos mailboxes
    const mail = [
      { x: 11.9, z: -7.5 },
      { x: -48.8, z: -20 },
      { x: 40.7, z: 8.6 },
    ];
    instanced(
      mergeAny([
        tint(new RoundedBoxGeometry(0.42, 0.7, 0.36, 2, 0.08).translate(0, 0.75, 0), 0xf2c014),
        tint(new THREE.CylinderGeometry(0.05, 0.05, 0.4, 8).translate(0, 0.2, 0), 0x3a3a3a),
        tint(new THREE.BoxGeometry(0.3, 0.03, 0.02).translate(0, 0.95, 0.19), 0x1d1d1d),
      ]),
      P(0xffffff, { vertexColors: true, roughness: 0.35, metalness: 0.2 }),
      mail,
      (o, m) => o.position.set(m.x, 0.36, m.z),
    );
  }
}
